import { FormEvent, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useEditThermostatMutation, useGetThermostatBySnQuery } from "store/api/devicesApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setCurrentZip } from "store/tempDataSlise"
import { deviceSystemTypeOptions, hvacOptions } from "constants/SelectFieldOptions"
import { ApiTypes, UITypes } from "types"
import DevicePhysicalStatus from "types/enums/DevicePhysicalStatus"
import { ReactComponent as ThermostatIcon } from "assets/svg/thermostat.svg"

import { convertDataToOptions } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import {
  editThermostatInventorySchema,
  editThermostatSchema,
  onFormValidate,
  validationInitialState,
} from "utils/onValidate"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import { Spinner } from "components/common/Spinner"

import AddressInformationSection from "./AddressInformationSection"
import NotesSection from "./NotesSection"
import SystemInformationSection, { systemAgeOptions } from "./SystemInformationSection"
import { EditThermostatRequestBody } from "./types"

const ModalContent = styled.div`
  padding: 20px;
  max-height: calc(90vh - 145px);
  max-height: calc(var(--vh, 1vh) * 90 - 145px);
  overflow-y: auto;
  overflow-x: hidden;
`

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
`

interface FormValues {
  address1: string
  address2: string
  country: UITypes.Option[]
  zip: UITypes.Option[]
  state: UITypes.Option[]
  city: UITypes.Option[]
  suburb: UITypes.Option[]
  systemType: UITypes.Option[]
  installationType: UITypes.Option[]
  systemAge: UITypes.Option[]
  notes: string
}

const initialFormValues: FormValues = {
  address1: "",
  address2: "",
  country: [],
  zip: [],
  state: [],
  city: [],
  suburb: [],
  systemType: [],
  installationType: [],
  systemAge: [],
  notes: "",
}

interface Props {
  device: Pick<ApiTypes.Model.Device, "sn" | "phisical_status">
  onClose: () => void
  onSuccess: () => void
}

const findOption = (value: string | number | undefined, options: UITypes.Option[]): UITypes.Option[] =>
  value != null ? options.filter(o => o.value === value) : []

const toSingleOption = <K extends string, T extends { id: number } & Record<K, string | number>>(
  item: T | null | undefined,
  labelKey: K,
): UITypes.Option[] => (item?.id ? convertDataToOptions({ data: [item] }, "id", labelKey) : [])

export const EditThermostatModal = ({ device, onClose, onSuccess }: Props) => {
  const dispatch = useAppDispatch()
  const useInventorySchema = [
    DevicePhysicalStatus.PREINSTALLED,
    DevicePhysicalStatus.INVENTORY,
    DevicePhysicalStatus.WARRANTY,
    DevicePhysicalStatus.TRANSFERRING,
  ].includes(device.phisical_status as DevicePhysicalStatus)
  const canEditSystemType = [DevicePhysicalStatus.PREINSTALLED, DevicePhysicalStatus.INVENTORY].includes(
    device.phisical_status as DevicePhysicalStatus,
  )
  const [formValues, setFormValues] = useState<FormValues>(initialFormValues)
  const [error, setError] = useState(validationInitialState)
  const [editThermostat, { isLoading }] = useEditThermostatMutation()
  const { data: deviceData, isFetching: isFetchingDevice } = useGetThermostatBySnQuery(device.sn, {
    refetchOnMountOrArgChange: true,
  })
  const currentZip = useAppSelector(state => state.tempData.currentZip)

  useEffect(() => {
    dispatch(setCurrentZip(undefined))
  }, [dispatch])

  useEffect(() => {
    if (!deviceData) return

    setFormValues(prev => ({
      ...prev,
      address1: deviceData.address1 || "",
      address2: deviceData.address2 || "",
      country: toSingleOption(deviceData.country, "name"),
      zip: toSingleOption(deviceData.zip, "code"),
      state: toSingleOption(deviceData.state, "name"),
      city: toSingleOption(deviceData.city, "name"),
      suburb: toSingleOption(deviceData.suburb, "name"),
      systemType: findOption(deviceData.system_type, deviceSystemTypeOptions),
      installationType: findOption(deviceData.installation_type, hvacOptions),
      systemAge: findOption(
        deviceData.system_age != null ? String(deviceData.system_age) : undefined,
        systemAgeOptions,
      ),
      notes: deviceData.notes || "",
    }))
  }, [deviceData])

  useEffect(() => {
    if (!currentZip?.city) return
    const city = convertDataToOptions({ data: [currentZip.city] }, "id", "name")
    const state = convertDataToOptions({ data: [currentZip.state] }, "id", "name")
    const country = convertDataToOptions({ data: [currentZip.country] }, "id", "name")
    const suburb = convertDataToOptions(
      { data: currentZip.suburbs?.length > 0 ? [currentZip.suburbs[0]] : null },
      "id",
      "name",
    )

    if (city && state) {
      setFormValues(prev => ({ ...prev, city, state, country, ...(suburb?.length ? { suburb } : {}) }))
    }
  }, [currentZip])

  const onInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setFormValues(prev => ({ ...prev, [name]: value }))
  }

  const handleCountryChange = (country: UITypes.Option[]) => {
    if (formValues.country?.[0]?.value === country[0]?.value) {
      return
    }
    dispatch(setCurrentZip(undefined))
    setFormValues(prev => ({ ...prev, country, zip: [], state: [], city: [], suburb: [] }))
  }

  const handleFieldChange = (field: string, value: UITypes.Option[]) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value,
      ...(field === "installationType" && value[0]?.value === "new" ? { systemAge: [] } : {}),
    }))
  }

  const handleCancel = () => {
    dispatch(setCurrentZip(undefined))
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault()

    const schema = useInventorySchema ? editThermostatInventorySchema : editThermostatSchema
    const isValid = onFormValidate(formValues, schema, setError)
    if (!isValid) return

    const body: EditThermostatRequestBody = {
      address1: formValues.address1,
      address2: formValues.address2 || undefined,
      country: Number(formValues.country[0]?.value),
      zip_code: formValues.zip[0]?.label as string,
      state: formValues.state?.[0] ? Number(formValues.state[0].value) : undefined,
      city: formValues.city?.[0] ? Number(formValues.city[0].value) : undefined,
      suburb: formValues.suburb?.[0]?.value ? String(formValues.suburb[0].value) : undefined,
      system_type: canEditSystemType ? (formValues.systemType?.[0]?.value as string | undefined) : undefined,
      installation_type: formValues.installationType?.[0]?.value as string | undefined,
      system_age:
        formValues.installationType?.[0]?.value === "new"
          ? null
          : formValues.systemAge?.[0]
            ? Number(formValues.systemAge[0].value)
            : undefined,
      notes: formValues.notes || undefined,
    }

    const res = await editThermostat({ sn: device.sn, body })

    const errorMessage = getErrorMessage(res?.error)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    toast.success("Thermostat updated successfully")
    dispatch(setCurrentZip(undefined))
    onSuccess()
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="620px"
          confirmButtonText="Save"
          confirmButtonType="submit"
          onConfirm={handleSubmit}
          onCancel={handleCancel}
          buttonSize="auto"
          isFetching={isLoading}
        >
          <ModalHeader title="Edit Thermostat" subtitle={device.sn}>
            <ThermostatIcon />
          </ModalHeader>
          <ModalContent>
            {isFetchingDevice ? (
              <LoaderWrapper>
                <Spinner />
              </LoaderWrapper>
            ) : (
              <>
                <AddressInformationSection
                  formValues={formValues}
                  error={error}
                  onInputChange={onInputChange}
                  onCountryChange={handleCountryChange}
                  onFieldChange={handleFieldChange}
                />
                <SystemInformationSection
                  sn={device.sn}
                  systemType={formValues.systemType}
                  installationType={formValues.installationType}
                  systemAge={formValues.systemAge}
                  isPreInstallation={canEditSystemType}
                  error={error}
                  onFieldChange={handleFieldChange}
                />
                <NotesSection
                  notes={formValues.notes}
                  onChange={value => setFormValues(prev => ({ ...prev, notes: value }))}
                />
              </>
            )}
          </ModalContent>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
